import React, { useState, useMemo, useEffect } from 'react';
import { Card, Form, Button, Select, DatePicker, Table, Checkbox, App, Spin, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePurchaseRequisitions } from '@/hooks/usePurchaseRequisition';
import purchaseRequisitionService from '@/services/purchaseRequisition.service';
import { supplierInquiryService, type CreateSupplierInquiryPayload } from '@/services/supplierInquiry.service';
import api from '@/services/api.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { uiPlainCurrencyLabel } from '@/constants/uaDirhamDisplay';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { LineSpecificationsText } from '@/components/procurement/LineSpecifications';

interface SupplierOption {
  id: string;
  supplier_code: string;
  supplier_name: string;
  is_active?: boolean;
}

const CreateSupplierInquiryPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [selectedPrIds, setSelectedPrIds] = useState<string[]>([]);
  const [selectedLineIds, setSelectedLineIds] = useState<string[]>([]);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState<string[]>([]);

  const { data: prList = [], isLoading: prLoading } = usePurchaseRequisitions({ status: 'Approved' });
  const { data: allPrList = [] } = usePurchaseRequisitions({});
  const prsForSelect = (allPrList.length ? allPrList : prList).filter((p: any) => p.pr_status !== 'Cancelled');

  const { data: suppliersRaw } = useQuery({
    queryKey: ['procurement-suppliers-list'],
    queryFn: async () => {
      const res = await api.get('/procurement/suppliers');
      const d = res?.data?.data ?? res?.data ?? res;
      return Array.isArray(d) ? d : (d?.data ?? []);
    },
  });
  const suppliers: SupplierOption[] = useMemo(() => {
    const list = Array.isArray(suppliersRaw) ? suppliersRaw : [];
    return list.filter((s) => s.is_active !== false);
  }, [suppliersRaw]);

  const { baseCurrency, allCurrencies, loading: currenciesLoading } = useCurrency();
  const currencyOptions = useMemo(() => {
    const active = (allCurrencies || []).filter((c) => c.is_active);
    if (active.length > 0) {
      return active.map((c) => {
        const d = uiPlainCurrencyLabel(c.symbol, c.currency_code);
        const label =
          d === c.currency_code ? `${c.currency_code} (${c.currency_name})` : `${d} ${c.currency_code}`;
        return { value: c.currency_code, label };
      });
    }
    const base = baseCurrency?.currency_code ?? 'AED';
    return [{ value: base, label: baseCurrency?.currency_name ? `${base} (${baseCurrency.currency_name})` : base }];
  }, [allCurrencies, baseCurrency]);

  const prsWithItems = useQuery({
    queryKey: ['pr-details-for-inquiry', selectedPrIds],
    queryFn: async () => {
      const arr = await Promise.all(selectedPrIds.map((id) => purchaseRequisitionService.getById(id).catch(() => null)));
      return arr.filter(Boolean);
    },
    enabled: selectedPrIds.length > 0,
  });

  const allLines = useMemo(() => {
    if (!prsWithItems.data) return [];
    const lines: {
      id: string;
      pr_id: string;
      pr_number: string;
      item_code?: string;
      item_description: string;
      /** Supplier-facing qty (pieces for measured lines; else PR requested). */
      requested_quantity: number;
      uom?: string;
      project_name?: string;
      specifications?: string;
    }[] = [];
    for (const pr of prsWithItems.data as any[]) {
      if (!pr?.items) continue;
      for (const it of pr.items) {
        const measured =
          Boolean(String(it.measurement_type ?? '').trim()) && Number(it.piece_quantity) > 0;
        lines.push({
          id: it.id,
          pr_id: pr.id,
          pr_number: pr.pr_number,
          item_code: it.item_code,
          item_description: it.item_description,
          // Match backend supplierFacingQuantityFromPrItem — not internal stock total.
          requested_quantity: measured ? Number(it.piece_quantity) : Number(it.requested_quantity),
          uom: measured ? 'PCS' : it.uom,
          project_name: it.project?.project_name ?? it.project_name ?? '—',
          specifications: it.specifications,
        });
      }
    }
    return lines;
  }, [prsWithItems.data]);

  useEffect(() => {
    const first = (prsWithItems.data as any[] | undefined)?.[0];
    const deptId = first?.department_id;
    if (deptId && !form.getFieldValue('department_id')) {
      form.setFieldValue('department_id', deptId);
    }
  }, [prsWithItems.data, form]);

  const createMutation = useMutation({
    mutationFn: (payload: CreateSupplierInquiryPayload) => supplierInquiryService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-inquiries'] });
      message.success('Inquiry created');
      navigate('/scm/procurement/supplier-inquiries');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Create failed'),
  });

  const handleSubmit = async () => {
    const values = await form.validateFields();
    if (selectedLineIds.length === 0) {
      message.error('Select at least one PR line');
      return;
    }
    if (selectedSupplierIds.length === 0) {
      message.error('Select at least one supplier');
      return;
    }
    const payload: CreateSupplierInquiryPayload = {
      inquiry_date: values.inquiry_date.format('YYYY-MM-DD'),
      response_deadline: values.response_deadline?.format('YYYY-MM-DD'),
      expected_delivery_date: values.expected_delivery_date?.format('YYYY-MM-DD'),
      currency: values.currency || 'AED',
      remarks: values.remarks,
      department_id: values.department_id,
      purchase_requisition_ids: selectedPrIds,
      pr_line_ids: selectedLineIds,
      supplier_ids: selectedSupplierIds,
    };
    createMutation.mutate(payload);
  };

  const lineColumns: ColumnsType<any> = [
    {
      title: 'Include',
      width: 70,
      render: (_, r) => (
        <Checkbox
          checked={selectedLineIds.includes(r.id)}
          onChange={(e) => setSelectedLineIds((prev) => (e.target.checked ? [...prev, r.id] : prev.filter((id) => id !== r.id)))}
        />
      ),
    },
    { title: 'PR', dataIndex: 'pr_number', width: 120 },
    { title: 'Item', dataIndex: 'item_code', width: 100 },
    {
      title: 'Description',
      dataIndex: 'item_description',
      render: (desc: string, r) => (
        <>
          <div>{desc}</div>
          <LineSpecificationsText value={r.specifications} />
        </>
      ),
    },
    { title: 'Qty', dataIndex: 'requested_quantity', width: 80, align: 'right' },
    { title: 'UOM', dataIndex: 'uom', width: 60 },
    { title: 'Project', dataIndex: 'project_name', width: 140, ellipsis: true },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/procurement/supplier-inquiries')} style={{ marginBottom: 16 }}>
        Back
      </Button>
      <Card title="Create Supplier Inquiry">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ currency: baseCurrency?.currency_code ?? 'AED', inquiry_date: dayjs() }}
        >
          <Form.Item name="inquiry_date" label="Inquiry Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="response_deadline" label="Response Deadline">
            <DatePicker style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="expected_delivery_date" label="Expected Delivery">
            <DatePicker style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="currency" label="Currency" rules={[{ required: true, message: 'Select currency' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              loading={currenciesLoading}
              placeholder="Select currency"
              style={{ width: 280 }}
              options={currencyOptions}
            />
          </Form.Item>
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
          >
            <DepartmentSelect style={{ width: 280 }} />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} placeholder="Optional" />
          </Form.Item>
          <Form.Item label="Purchase Requests" required>
            <Select
              mode="multiple"
              placeholder="Select PR(s)"
              style={{ width: '100%' }}
              value={selectedPrIds}
              onChange={setSelectedPrIds}
              options={prsForSelect.map((p: any) => ({ value: p.id, label: `${p.pr_number} - ${p.pr_date}` }))}
              loading={prLoading}
            />
          </Form.Item>
          {selectedPrIds.length > 0 && (
            <Form.Item label="PR lines to include" required>
              {prsWithItems.isLoading ? (
                <Spin />
              ) : (
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={allLines}
                  columns={lineColumns}
                  pagination={false}
                  scroll={{ y: 200 }}
                />
              )}
            </Form.Item>
          )}
          <Form.Item label="Suppliers" required>
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              filterOption={(input, opt) =>
                (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
              placeholder="Search and select supplier(s)"
              style={{ width: '100%' }}
              value={selectedSupplierIds}
              onChange={setSelectedSupplierIds}
              options={suppliers.map((s) => ({ value: s.id, label: `${s.supplier_code} - ${s.supplier_name}` }))}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={createMutation.isPending}>
              Create Inquiry
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => navigate('/scm/procurement/supplier-inquiries')}>
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default CreateSupplierInquiryPage;
